import React, { useState, useEffect, useId, useRef } from 'react';
import { Modal } from './Modal';
import { User, Channel, OnlineUser, handleAvatarError, CHANNEL_NAME_MAX_LEN } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { channelService } from '../services/channelService';
import { useAffiliateUserSearch } from '../hooks/useAffiliateUserSearch';
import { UserPickerRow } from './UserPickerRow';
import { formatGroupName } from '../utils/groupName';
import {
  Users,
  Search,
  X,
  Loader2,
  UserPlus,
  Building2,
  Building,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: User;
  onlineUsers: OnlineUser[];
  onGroupCreated?: (channel: Channel) => void;
  onStartDM?: (user: User) => Promise<boolean>;
}

// 채널명 input의 maxLength — 서버 검증·이름 변경 모달과 공유하는 상수의 별칭
const MAX_NAME_LEN = CHANNEL_NAME_MAX_LEN;

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onlineUsers,
  onGroupCreated,
  onStartDM,
}) => {
  // 1단계: 멤버 선택 / 2단계: 그룹 정보 입력 (1명 선택 시 2단계 없이 즉시 DM)
  const fid = useId();
  const [step, setStep] = useState<1 | 2>(1);

  // 폼 상태
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 계열사별 사용자 검색 훅
  const {
    affiliates,
    selectedAffiliateId,
    setSelectedAffiliateId,
    showAffiliateSelector,
    filteredUsers,
    selectedUsers,
    usersByDepartment,
    loading,
    error: loadError,
    searchQuery,
    setSearchQuery,
    toggleUser,
    removeUser,
    selectDepartment,
    reset,
  } = useAffiliateUserSearch({
    isActive: isOpen,
    currentUserId: currentUser.id,
  });

  // 모달 닫을 때 초기화
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setGroupName('');
      setDescription('');
      setError(null);
      reset();
    }
  }, [isOpen, reset]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // 검색 중에 사람을 고르면 검색어를 비운다 — 다음 사람을 찾으려고 지우고 다시 칠 필요가 없게.
  // 터치 기기는 행을 누를 때 키보드가 내려가므로 다시 포커스하지 않는다(닫혔다 열리며 화면이 출렁인다)
  const clearSearchAfterPick = () => {
    if (!searchQuery) return;
    setSearchQuery('');
    if (!IS_COARSE_POINTER) searchInputRef.current?.focus();
  };

  // 체크 해제는 검색어를 유지한다(잘못 누른 것을 고치는 중이라 검색 결과가 그대로 있어야 한다)
  const handlePick = (userId: string | number) => {
    const picking = !filteredUsers.find((u) => String(u.id) === String(userId))?.selected;
    toggleUser(userId);
    if (picking) clearSearchAfterPick();
  };

  // 이름 칸을 비웠을 때 내 화면에 보일 이름(나를 뺀 멤버, 카카오톡 방식). 다른 멤버에게는 서버가
  // 그 사람 기준으로 따로 계산해 보여 준다(server/utils/groupName.js — 이건 미리보기용 사본)
  const autoName = formatGroupName(selectedUsers.map((u) => u.username));

  // 1단계 "다음": 1명이면 즉시 DM 개설, 2명 이상이면 그룹 정보 입력 단계로
  const handleNext = async () => {
    if (selectedUsers.length === 0) return;

    if (selectedUsers.length === 1) {
      setCreating(true);
      setError(null);
      try {
        // 채팅방 열기(채널 추가·활성화·모바일 전환)는 App의 handleUserClick이 수행
        const ok = await onStartDM?.(selectedUsers[0]);
        if (ok) {
          onClose();
        } else {
          setError('1:1 채팅을 시작하지 못했습니다. 다시 시도해주세요.');
        }
      } finally {
        setCreating(false);
      }
      return;
    }

    setError(null);
    setStep(2);
  };

  // 그룹 생성 (2단계에서만 호출 — 멤버 2명 이상 보장)
  const handleCreate = async () => {
    if (selectedUsers.length === 0) {
      setError('최소 1명의 멤버를 선택해주세요.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const members = selectedUsers.map((u) => ({
        id: String(u.id),
        name: u.username,
      }));

      // 이름을 비우면 자동 이름(nameAuto) — 서버가 보는 사람마다 '나를 뺀 멤버' 이름으로 보여 주고
      // 들고 날 때 바꾼다. 미리보기 이름도 함께 보내는 것은 nameAuto를 모르는 구서버 호환용이다.
      const typedName = groupName.trim();
      const finalName = typedName || autoName;

      const channel = await channelService.createGroupChannel(
        finalName,
        String(currentUser.id),
        members,
        description.trim() || undefined,
        !typedName
      );

      // 멤버 통지는 서버(POST /api/channels)가 직접 보낸다.
      // 여기서 소켓으로 보강하던 방식은 소켓이 끊겨 있으면 조용히 유실됐다.

      onGroupCreated?.(channel);
      onClose();
    } catch (err) {
      console.error('Failed to create group:', err);
      setError('채팅방 생성에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="채팅방 개설"
      size="lg"
      // 1단계는 사용자 목록이 자체 스크롤을 갖는다 → 본문 스크롤을 끄고 목록에 높이를 넘긴다
      bodyFlex={step === 1}
      footer={
        <div className="flex items-center justify-between">
          {step === 1 ? (
            <p className="text-sm text-slate-400">
              {selectedUsers.length}명 선택됨
            </p>
          ) : (
            <button
              onClick={() => setStep(1)}
              disabled={creating}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <ArrowLeft size={16} />
              이전
            </button>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              취소
            </button>
            {step === 1 ? (
              <button
                onClick={handleNext}
                disabled={creating || selectedUsers.length === 0}
                className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    개설 중...
                  </>
                ) : (
                  <>
                    다음 ({selectedUsers.length})
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    개설 중...
                  </>
                ) : (
                  <>
                    <Users size={16} />
                    개설
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
      <div className={`flex flex-col gap-5 ${step === 1 ? 'flex-auto min-h-0' : ''}`}>
        {/* 에러 메시지 */}
        {error && (
          <div className="p-3 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 text-sm">
            {error}
          </div>
        )}

        {step === 1 ? (
          <>
            {/* 선택된 멤버 태그 */}
            {selectedUsers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  선택된 멤버
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-100 rounded-lg max-h-24 overflow-y-auto">
                  {selectedUsers.map((user) => (
                    <span
                      key={user.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-50 text-brand-600 rounded-full text-sm"
                    >
                      <img
                        src={resolveAvatarUrl(user.avatar)} onError={handleAvatarError}
                        alt=""
                        className="w-4 h-4 rounded-full"
                      />
                      {user.username}
                      <button
                        onClick={() => removeUser(user.id)}
                        className="hover:bg-brand-100 rounded-full p-0.5 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 계열사 선택 + 멤버 검색 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                대화상대 선택 <span className="text-brand-600">*</span>
              </label>

              {/* 계열사 선택 드롭다운 */}
              {showAffiliateSelector && (
                <div className="mb-2">
                  <div className="relative">
                    <Building
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <select aria-label="대화상대 계열사"
                      value={selectedAffiliateId}
                      onChange={(e) => setSelectedAffiliateId(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors appearance-none cursor-pointer"
                    >
                      {affiliates.map((aff) => (
                        <option key={aff.id} value={aff.id}>
                          {aff.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input aria-label="이름 또는 부서로 검색"
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="이름 또는 부서로 검색"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors"
                />
              </div>
            </div>

            {/* 사용자 목록 로드 에러 */}
            {loadError && (
              <div className="px-3 py-2 mb-2 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 text-sm">
                {loadError}
              </div>
            )}

            {/* 사용자 목록 */}
            <div className="border border-slate-200 rounded-lg overflow-hidden flex-auto min-h-0 flex flex-col">
              <div className="flex-auto min-h-0 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8 text-slate-400">
                    <Loader2 size={24} className="animate-spin mr-2" />
                    불러오는 중...
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                    <UserPlus size={32} className="mb-2 opacity-50" />
                    <p>{loadError ? '목록을 불러오지 못했습니다' : '검색 결과가 없습니다'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {usersByDepartment.map(([deptName, users]) => (
                      <div key={deptName}>
                        {/* 부서 헤더 */}
                        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-white/90 backdrop-blur-sm border-b border-slate-100">
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Building2 size={14} />
                            {deptName}
                            <span className="text-xs opacity-60">
                              ({users.length})
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              selectDepartment(deptName);
                              clearSearchAfterPick();
                            }}
                            className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
                          >
                            전체 선택
                          </button>
                        </div>

                        {/* 부서원 목록 */}
                        {users.map((user) => (
                          <UserPickerRow
                            key={user.id}
                            user={user}
                            onlineUsers={onlineUsers}
                            onToggle={handlePick}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 대화상대 요약 (읽기 전용) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                대화상대
              </label>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-600">
                {autoName} · {selectedUsers.length}명
              </div>
            </div>

            {/* 채팅방 이름 (선택) */}
            <div>
              <label htmlFor={`${fid}-name`} className="block text-sm font-medium text-slate-700 mb-2">
                채팅방 이름 <span className="text-slate-400">(선택)</span>
              </label>
              <input id={`${fid}-name`}
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder={autoName}
                maxLength={MAX_NAME_LEN}
                className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors"
              />
              <p className="mt-1.5 text-xs text-slate-500">
                비워 두면 각자에게 자기를 뺀 참여자 이름으로 보이고, 사람이 들고 나면 바뀝니다.
              </p>
            </div>

            {/* 설명 (선택) */}
            <div>
              <label htmlFor={`${fid}-desc`} className="block text-sm font-medium text-slate-700 mb-2">
                설명 <span className="text-slate-400">(선택)</span>
              </label>
              <input id={`${fid}-desc`}
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="채팅방에 대한 간단한 설명"
                maxLength={100}
                className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
